<template>
  <div class="main event-editor">
    <FormWrapper ref="the_form" :action="formAction" method="PUT">
      <div class="title-bar">
        <h1 class="mb-1">Edit Event</h1>
        <h4 class="text-primary mb-3">
          {{ event.name }} - {{ event.start_date | moment("MM/DD/YYYY") }} (ID:
          {{ event.id }})
        </h4>
        <a class="btn-back" :href="route('event-calendar.index')">
          <span class="icon icon-arrow-left"></span>
          <span class="text">Go Back</span>
        </a>
      </div>

      <div class="row no-gutters py-4">
        <div class="col-12 col-lg-8 col-xl-9 mb-5 mb-lg-0">
          <div class="row no-gutters px-3 pl-lg-4 pr-lg-0 pl-xl-5">
            <div class="col-12">
              <transition name="fade">
                <Alert v-if="hasAlert" :alert="alert" />
              </transition>
            </div>

            <div class="col-12">
              <div v-if="isDistributorOwned" class="row">
                <div class="col-12 mb-3">
                  <h3 class="text-center">Dates & Times</h3>
                </div>
                <div class="col-6 col-md-4 mb-3">
                  <Field
                    type="datepicker"
                    name="start_date"
                    label="Start Date *"
                    v-model="startDate"
                    class="vdp-left-align"
                  />
                </div>
                <div class="col-6 col-md-4 mb-3">
                  <Field
                    type="datepicker"
                    name="end_date"
                    label="End Date *"
                    v-model="endDate"
                    class="vdp-right-align"
                  />
                </div>
                <div class="col-12 col-md-4 mb-3">
                  <TimezoneField
                    name="timezone"
                    label="Time Zone *"
                    :value="event.timezone"
                  />
                </div>
                <div class="col-12">
                  <Field
                    type="checkbox"
                    name="use_same_time"
                    id="cb_use_same_time"
                    label="Use the same start & end time for each day of the event"
                    v-model="sameTimeEachDay"
                    :checkbox-value="1"
                    class="mb-3"
                  />
                  <EditEventTimes
                    v-model="eventTimes"
                    :start="startDate"
                    :end="endDate"
                    :same-time-each-day="sameTimeEachDay"
                  />
                </div>
                <div class="col-12 mb-1">
                  <hr />
                </div>
              </div>

              <div class="row" v-if="! $root.isExternalTrainer">
                <div class="col-12 mb-3">
                  <h3 class="text-center">Registration Settings</h3>
                </div>
                <div class="col-6 col-xl-3 mb-3">
                  <Field
                    type="datepicker"
                    name="registration_opens"
                    label="Registration Open Date *"
                    :value="event.registration_opens"
                    class="vdp-left-align"
                  />
                </div>
                <div class="col-6 col-xl-3 mb-3">
                  <Field
                    type="datepicker"
                    name="registration_closes"
                    label="Registration Close Date"
                    :value="event.registration_closes"
                    class="vdp-right-align"
                  />
                </div>
                <div class="col-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="price"
                    :label="`${priceLabelPrefix} Standard Price`"
                    :value="event.price"
                  />
                </div>
                <div class="col-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="fad_price"
                    :label="`${priceLabelPrefix} FAD/Elite Price`"
                    :value="event.fad_price"
                  />
                </div>
                <template v-if="isDistributorOwned">
                  <div class="col-12 col-sm-6 col-xl-3 mb-3">
                    <Field
                      type="text"
                      name="max_attendees"
                      label="Maximum Attendees"
                      :value="event.max_attendees"
                    />
                  </div>
                  <template v-if="!event.managers_only">
                    <div class="col-12 mb-3">
                      <Field
                        type="checkbox"
                        name="fad_only"
                        id="cb_fad_only"
                        label="FAD/Elite Only - only FAD/Elite dealers may register"
                        :value="event.fad_only || false"
                        :checkbox-value="1"
                        class="mb-3"
                      />
                      <Field
                        type="checkbox"
                        name="allow_dealer_enroll"
                        id="cb_allow_dealer_enroll"
                        label="Dealer Registration Enabled - dealers may register themselves"
                        :value="event.allow_dealer_enroll || false"
                        :checkbox-value="1"
                      />
                    </div>
                  </template>
                </template>
                <div class="col-12">
                  <MultiAssignField
                    type="select"
                    label="Brand"
                    name="brands"
                    :options="brands"
                    :value="event._brands || []"
                  />
                </div>
                <div class="col-12 mb-1">
                  <hr />
                </div>
              </div>

              <div v-if="isVirtual && event.contact_id != $root.user.id" class="row">
                <div class="col-12 mb-3">
                  <h3 class="text-center">Webinar Settings</h3>
                </div>
                <div class="col-12 mb-3">
                  <Field
                    type="text"
                    name="webinar_url"
                    label="Webinar URL *"
                    :value="event.webinar_url"
                  />
                </div>
                <div class="col-12 mb-3" v-if="! $root.isExternalTrainer">
                  <HtmlField
                    name="webinar_notes"
                    label="Additonal Webinar Details"
                    :value="event.webinar_notes"
                  />
                </div>
              </div>

              <div v-if="!isVirtual && !$root.isExternalTrainer" class="row">
                <div class="col-12 mb-3">
                  <h3 class="text-center">Location</h3>
                </div>
                <div class="col-12 col-xl-4 mb-3">
                  <Field
                    type="text"
                    name="location"
                    label="Location / Venue"
                    v-model="event.location"
                  />
                </div>
                <div class="col-12 col-xl-4 mb-3">
                  <Field
                    type="text"
                    name="address1"
                    label="Address Line 1"
                    v-model="event.address1"
                  />
                </div>
                <div class="col-12 col-xl-4 mb-3">
                  <Field
                    type="text"
                    name="address2"
                    label="Address Line 2"
                    v-model="event.address2"
                  />
                </div>
                <div class="col-12 col-sm-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="city"
                    label="City"
                    v-model="event.city"
                  />
                </div>
                <div class="col-12 col-sm-6 col-xl-3 mb-3">
                  <StateField
                    name="state"
                    label="State/Province"
                    v-model="event.state"
                  />
                </div>
                <div class="col-12 col-sm-6 col-xl-3 mb-3">
                  <Field
                    type="text"
                    name="zip"
                    label="Zip/Postal Code"
                    v-model="event.zip"
                  />
                </div>
                <div class="col-12 col-sm-6 col-xl-3 mb-3">
                  <CountryField
                    name="country"
                    label="Country"
                    v-model="event.country"
                  />
                </div>
              </div>
            </div>
          </div>
        </div>

        <div class="col-12 col-lg-4 col-xl-3 form-button-wrapper">
          <div class="sticky-top">
            <div class="row no-gutters text-center">
              <div class="col-12 pb-3">
                <button type="submit" class="btn btn-success w-50">
                  Save Changes
                </button>
              </div>
              <div class="col-12 pb-3">
                <a :href="route('event-calendar.index')" class="btn btn-info w-50">
                  Cancel & Exit
                </a>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FormWrapper>
  </div>
</template>

<script>
import DisplaysAlerts from "mixins/DisplaysAlerts";
import EditEventTimes from "../../admin/events/partials/EditEventTimes";

export default {
  mixins: [DisplaysAlerts],

  components: { EditEventTimes },

  props: {
    event: { type: Object, required: true },
    brands: { type: Array, default: () => [] },
  },

  data() {
    return {
      startDate: null,
      endDate: null,
      sameTimeEachDay: false,
      eventTimes: [],
    };
  },

  computed: {
    routeParam() {
      return { event: this.event.id };
    },
    formAction() {
      return this.route("training.events.update", this.routeParam);
    },
    isVirtual() {
      return !!this.event.course.is_virtual;
    },
    isDistributorOwned() {
      return (
        this.event.course.owner_type == this.$root.constants.DISTRIBUTOR_OWNED
      );
    },
    priceLabelPrefix() {
      return this.isDistributorOwned ? "Distributor" : "Factory";
    },
  },

  watch: {
    endDate(val) {
      var startDate =
        typeof this.startDate == "string"
          ? new Date(this.startDate)
          : _.cloneDeep(this.startDate);
      var endDate =
        typeof this.endDate == "string"
          ? new Date(this.endDate)
          : _.cloneDeep(this.endDate);
      if (startDate > endDate) {
        alert("End Date cannot occur before Start Date");
      }
    },
  },

  mounted() {
    this.startDate = this.event.start_date || new Date();
    this.endDate = this.event.end_date || new Date();
    this.eventTimes = _.cloneDeep(this.event.times || []);
  },
};
</script>